/**
 * Da mesa na tela (pixels) para a mesa da física (unidades de cena).
 *
 * A câmera olha de cima e o chão enche exatamente a área da mesa: as bordas da
 * tela são as paredes onde os dados batem. O tamanho do dado acompanha a altura da
 * mesa (no original, um dado parado ocupa perto de um décimo da altura).
 */
import { DIE_RADIUS, type TableSpec } from './stage.js';

/** Campo de visão vertical da câmera (graus): pequeno, quase sem distorção. */
export const CAMERA_FOV = 28;

export type TableView = TableSpec & {
  /** pixels por unidade de cena */
  pixelsPerUnit: number;
};

/** Diâmetro do dado em pixels para uma mesa desta altura. */
export function diePixels(height: number): number {
  return Math.max(54, Math.min(108, height * 0.09));
}

/**
 * Mesa de `width` × `height` px. `hand` é o ponto (px, relativo à mesa) de onde os
 * dados saem; sem ele, o canto de baixo à direita (onde fica o chat no layout padrão).
 */
export function tableView(width: number, height: number, hand?: { x: number; y: number }): TableView {
  const pixelsPerUnit = diePixels(height) / (2 * DIE_RADIUS);
  const halfWidth = width / 2 / pixelsPerUnit;
  const halfDepth = height / 2 / pixelsPerUnit;
  const from = hand ?? { x: width * 0.82, y: height * 0.85 };
  const cameraHeight = halfDepth / Math.tan(((CAMERA_FOV / 2) * Math.PI) / 180);
  const toScene = (x: number, y: number) => ({ x: (x - width / 2) / pixelsPerUnit, z: (y - height / 2) / pixelsPerUnit });
  return {
    halfWidth,
    halfDepth,
    hand: toScene(from.x, from.y),
    // atravessa a mesa na diagonal, pela dimensão comprida: da mão para o outro lado
    // (como no original, que joga do chat para cima e para a esquerda)
    aim: toScene(from.x >= width * 0.4 ? width * 0.24 : width * 0.76, height * 0.5),
    pixelsPerUnit,
    cameraHeight,
    // em mesa pequena a câmera fica baixa: o teto acompanha, para o dado não passar dela
    ceiling: Math.min(12, cameraHeight * 0.45),
  };
}
